import { OverflowTooltip, UserAvatar } from '@/components';
import { RoleColumn } from '@/components/Columns/RoleColumn';
import { SortableColumnDef } from '@/components/TableV2/SortableTable';
import { ToggleColumn } from '@/components/ToggleColumn';
import { UserStatusColumn } from '@/components/UserStatusColumn/UserStatusColumn';
import { RoutePath } from '@/core/router';
import { useToggleUserAccess } from '@/hooks/use-toggle-user-access';
import { useToggleUserStatus } from '@/hooks/use-toggle-user-status';
import { CompanyEmployee, OrganizationEmployee } from '@/modules/Users/domain';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

type Employee = OrganizationEmployee | CompanyEmployee;

export const useEmployeesColumns = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { onChange: onStatusChange } = useToggleUserStatus();
  const { onChange: onAccessChange } = useToggleUserAccess();

  return useMemo<SortableColumnDef<Employee>[]>(
    () => [
      {
        id: 'name',
        name: t('name'),
        accessor: 'fullName',
        alignment: 'left',
        tdClassNames: 'max-w-[15vh]',
        renderRow: (row) => {
          return (
            <div
              className="flex items-center gap-2.5 overflow-hidden"
              onClick={() => navigate(RoutePath.userDetail(row.id.toString()))}
            >
              <UserAvatar
                size="small"
                name={row.fullName}
                imageId={row.profileImageFileId}
              />
              <OverflowTooltip text={row.fullName} textClassName="font-black" />
            </div>
          );
        },
      },
      {
        id: 'email',
        name: t('email'),
        accessor: 'email',
      },
      {
        id: 'status',
        name: t('status'),
        accessor: 'status',
        renderRow: (row) => (
          <UserStatusColumn
            onChange={() => onStatusChange(!row.status, row.id)}
            row={row}
            value={row.status}
            userId={row.id}
          />
        ),
      },
      {
        id: 'access',
        name: t('access'),
        accessor: 'access',
        renderRow: (row) => (
          <ToggleColumn
            onChange={() => onAccessChange(!row.access, row.id)}
            row={row}
            value={row.access}
          />
        ),
      },
      {
        id: 'role',
        name: t('role'),
        accessor: 'roleName',
        renderRow: (row) => (
          <RoleColumn roleName={row.roleName} roleEntity={row.roleEntity} />
        ),
      },
    ],
    [navigate, onAccessChange, onStatusChange, t],
  );
};
